// Standby calculator – form, results, chart.
import { el, append, replace, debounce, nextId } from '../core/dom.js';
import { fmt, NNBSP } from '../core/format.js';
import { NumberField, Stepper, Segmented, ResultCards, SourceBadge, ShareBar, Table, Disclosure, ResetLink, Section } from '../core/ui.js';
import { barChart, Figure, responsiveChart } from '../core/svg.js';
import { saveStored, pageUrl } from '../core/state.js';
import { track } from '../core/track.js';
import { calculate, chartRows, usableItems } from './calc.js';
import { decodeState, encodeState, defaults, LIMITS } from './state.js';

const TOOL = 'standby';
const w = (v) => fmt(v, { min: 0, max: v < 10 ? 2 : 1 });
// Only the user's own value gets a badge; the method behind the values is explained once in the page text.
const badge = (source) => (source === 'eigen' ? SourceBadge(source) : '');

export function render(root, data, config, initialQuery) {
  let state = decodeState(data, initialQuery, config);
  const items = usableItems(data);
  const controls = {}; // id -> { stepper, own, ownWrap, badgeSlot, valueSlot }
  const groupMeta = {};
  const groupHourFields = {};

  const recalc = debounce(update, 150);
  let touched = false; // first user input shows the dock even before scrolling into the groups
  const commit = () => {
    touched = true;
    recalc();
  };

  // ---- Top settings
  // Entered in ct/kWh like on the bill; state, storage and tool_config stay in €/kWh.
  const [minCt, maxCt] = LIMITS.price.map((p) => Math.round(p * 100));
  const priceField = NumberField({
    label: 'Strompreis',
    unit: 'ct/kWh',
    value: state.price * 100,
    decimals: 2,
    hint: 'Arbeitspreis brutto, steht auf deiner Stromrechnung.',
    // Own range check instead of min/max: „0,35“ is the obvious slip in a cent field.
    validate: (ct) => {
      if (ct > 0 && ct < 1) return `Meinst du ${fmt(ct * 100, { max: 2 })}${NNBSP}ct? Der Preis wird hier in Cent eingegeben.`;
      if (ct < minCt) return `Mindestens ${minCt}${NNBSP}ct/kWh.`;
      if (ct > maxCt) return `Höchstens ${maxCt}${NNBSP}ct/kWh.`;
      return '';
    },
    onChange: (ct) => {
      state.price = ct / 100;
      commit();
    },
  });
  const hoursMode = Segmented({
    label: 'Betriebsstunden',
    value: Object.keys(state.groupHours).length ? 'groups' : 'all',
    options: [
      { value: 'all', label: '24 h am Tag' },
      { value: 'groups', label: 'Je Gruppe festlegen' },
    ],
    onChange: (v) => {
      if (v === 'all') state.groupHours = {};
      else for (const g of data.groups) state.groupHours[g.id] = groupHourFields[g.id].value || 24;
      syncGroupHours();
      commit();
    },
  });

  // ---- Groups
  const groupNodes = data.groups.map((g, gi) => {
    const groupItems = items.filter((i) => i.group === g.id);
    if (!groupItems.length) return null;
    const meta = el('span', { class: 'tool-acc__meta' });
    groupMeta[g.id] = meta;
    const hasCounts = groupItems.some((i) => state.counts[i.id] > 0);
    const acc = Disclosure({ summary: [el('span', {}, g.label), meta], open: gi === 0 || hasCounts, className: 'standby-group' });
    const hoursField = NumberField({
      label: `Stunden pro Tag (${g.label})`,
      unit: 'h',
      value: state.groupHours[g.id] ?? 24,
      min: LIMITS.hours[0],
      max: LIMITS.hours[1],
      decimals: 1,
      onChange: (v) => {
        state.groupHours[g.id] = v;
        commit();
      },
    });
    groupHourFields[g.id] = hoursField;
    hoursField.node.classList.add('standby-group-hours');
    append(
      acc.body,
      [g.note ? el('p', { class: 'tool-help standby-group-note' }, g.note) : null,
      hoursField.node,
      el('ul', { class: 'standby-items' }, groupItems.map((item) => itemRow(item)))],
    );
    return acc.node;
  });

  function itemRow(item) {
    const hintId = nextId('sbhint');
    const valueSlot = el('span', { class: 'standby-item__watt' });
    const badgeSlot = el('span', { class: 'standby-item__badge' });
    const stepper = Stepper({
      label: item.label,
      value: state.counts[item.id] || 0,
      min: LIMITS.count[0],
      max: LIMITS.count[1],
      describedBy: hintId,
      onChange: (v) => {
        if (v > 0) state.counts[item.id] = v;
        else delete state.counts[item.id];
        commit();
      },
    });
    const own = NumberField({
      label: 'Dein Messwert',
      unit: 'W',
      value: state.custom[item.id],
      min: 0.01,
      max: LIMITS.watt[1],
      decimals: 2,
      onChange: (v) => {
        state.custom[item.id] = v;
        syncItem(item);
        commit();
      },
    });
    const ownToggle = el('button', { type: 'button', class: 'tool-linkbtn standby-item__edit', 'aria-expanded': 'false' }, 'Wert anpassen');
    const ownReset = el('button', { type: 'button', class: 'tool-linkbtn standby-item__edit' }, 'Standardwert');
    const ownWrap = el('div', { class: 'standby-item__own', hidden: true }, own.node, ownReset);
    ownToggle.addEventListener('click', () => {
      const open = ownWrap.hidden;
      ownWrap.hidden = !open;
      ownToggle.setAttribute('aria-expanded', String(open));
      if (open) own.input.focus();
    });
    ownReset.addEventListener('click', () => {
      delete state.custom[item.id];
      own.value = NaN;
      ownWrap.hidden = true;
      ownToggle.setAttribute('aria-expanded', 'false');
      syncItem(item);
      commit();
      ownToggle.focus();
    });
    controls[item.id] = { stepper, own, ownWrap, ownToggle, badgeSlot, valueSlot };
    syncItem(item);
    if (Number.isFinite(state.custom[item.id])) {
      ownWrap.hidden = false;
      ownToggle.setAttribute('aria-expanded', 'true');
    }
    return el(
      'li',
      { class: 'standby-item', dataset: { id: item.id } },
      stepper.node,
      el('p', { class: 'standby-item__meta', id: hintId }, valueSlot, ' ', badgeSlot, ' ', ownToggle),
      ownWrap,
    );
  }

  function syncItem(item) {
    const c = controls[item.id];
    const hasOwn = Number.isFinite(state.custom[item.id]);
    const value = hasOwn ? state.custom[item.id] : item.typ;
    c.valueSlot.textContent = `${w(value)}${NNBSP}W je Gerät` + (!hasOwn && item.min !== item.max ? ` (${w(item.min)}–${w(item.max)})` : '');
    replace(c.badgeSlot, badge(hasOwn ? 'eigen' : item.source));
  }

  function syncGroupHours() {
    const perGroup = hoursMode.value === 'groups';
    for (const g of data.groups) {
      const f = groupHourFields[g.id];
      if (!f) continue;
      f.node.hidden = !perGroup;
      f.value = state.groupHours[g.id] ?? 24;
    }
  }

  // ---- Output
  const results = ResultCards({ label: 'Ergebnis Grundlast' });
  const chartHost = el('div', { class: 'standby-chart' });
  const chartAlt = el('div', { class: 'visually-hidden' });
  const figure = Figure(chartHost, { caption: 'Anteil je Kategorie an der Grundlast' });
  const otherTags = el('ul', { class: 'standby-other__tags' });
  const otherNote = el('div', { class: 'standby-other' }, el('p', { class: 'standby-other__title' }, 'In „Sonstige“ zusammengefasst:'), otherTags);
  // Tags for „Sonstige“ inside the figure (media width), between chart and caption
  figure.insertBefore(otherNote, figure.querySelector('figcaption'));
  append(figure, [chartAlt]);
  const tableSlot = el('div', { class: 'standby-table' });
  const empty = el('p', { class: 'tool-help standby-empty' }, 'Zähle oben deine Geräte. Das Ergebnis erscheint sofort.');
  let result = calculate(data, state);

  const chart = responsiveChart(chartHost, (width) => {
    const bars = chartRows(result.rows);
    if (!bars.length) return null;
    return barChart(
      bars.map((b) => ({ label: b.label, value: b.watt, valueLabel: `${w(b.watt)} W · ${fmt(b.share * 100, 0)} %`, cls: b.other ? 'svg-bar--muted' : '' })),
      { label: 'Balkendiagramm: Anteil je Gerätekategorie an der Grundlast', width, labelWidth: 300, valueWidth: 120 },
    );
  });

  const settingsRow = el('div', { class: 'tool-row' }, priceField.node, hoursMode.node);
  const share = ShareBar({ getText: () => plainText(), onShare: (a) => track(TOOL, a) });
  const resultSection = Section('Ergebnis', results.node, empty, figure);
  resultSection.classList.add('standby-results');

  // ---- Dock: compact result pinned to the bottom – not on page load, but after the first input or once the
  // settings row has left the top of the viewport, and only while the result cards are still below.
  // Zero-height sticky anchor, so it takes no space in the layout; hidden from screen readers (results are live already).
  const dockNums = { watt: el('strong'), kwh: el('strong'), eur: el('strong') };
  const dockBar = el(
    'div',
    { class: 'standby-dock__bar', title: 'Zum Ergebnis' },
    el('span', { class: 'standby-dock__item' }, dockNums.watt, `${NNBSP}W`),
    el('span', { class: 'standby-dock__item' }, dockNums.kwh, `${NNBSP}kWh`),
    el('span', { class: 'standby-dock__item' }, dockNums.eur, `${NNBSP}€ im Jahr`),
  );
  const dock = el('div', { class: 'standby-dock', 'aria-hidden': 'true' }, dockBar);
  let inGroups = false;
  let resultsAhead = true;
  const syncDock = () => dock.classList.toggle('is-visible', (inGroups || touched) && resultsAhead && result.rows.length > 0);
  // Measured on scroll/resize, not with IntersectionObserver: a restored scroll position on reload jumps
  // from „below the viewport“ to „above“ without an intersection change, so no observer callback fires.
  let frame = 0;
  const placeDock = () => {
    frame = 0;
    inGroups = settingsRow.getBoundingClientRect().bottom < 80; // settings row hidden under the site's fixed header (≈ 65 px)
    resultsAhead = resultSection.getBoundingClientRect().top > window.innerHeight - 72;
    syncDock();
  };
  const schedulePlace = () => {
    if (!frame) frame = requestAnimationFrame(placeDock);
  };
  dockBar.addEventListener('click', () => {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    resultSection.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  });

  function update() {
    result = calculate(data, state);
    const q = encodeState(data, state, config);
    saveStored(TOOL, q);
    const hasRows = result.rows.length > 0;
    if (hasRows) track(TOOL, 'calculate');

    results.update(
      [
        { label: 'Grundlast', value: w(result.watt), unit: 'W' },
        { label: 'pro Jahr', value: fmt(result.kwh, result.kwh < 10 ? 1 : 0), unit: 'kWh' },
        { label: 'kostet dich', value: fmt(result.eur, result.eur < 10 ? 2 : 0), unit: '€ im Jahr' },
      ],
      result.hasSpread ? `Spanne je nach Gerät: ${w(result.min)}–${w(result.max)}${NNBSP}W` : '',
    );
    dockNums.watt.textContent = w(result.watt);
    dockNums.kwh.textContent = fmt(result.kwh, result.kwh < 10 ? 1 : 0);
    dockNums.eur.textContent = fmt(result.eur, result.eur < 10 ? 2 : 0);
    syncDock();

    for (const g of data.groups) {
      const m = groupMeta[g.id];
      if (!m) continue;
      const gr = result.groups[g.id];
      m.textContent = gr.count ? `${gr.count} ${gr.count === 1 ? 'Gerät' : 'Geräte'} · ${w(gr.watt)} W` : '';
    }

    empty.hidden = hasRows;
    figure.hidden = !hasRows;
    chart.update();
    const other = chartRows(result.rows).find((b) => b.other);
    replace(otherTags, other ? other.labels.map((l) => el('li', { class: 'standby-other__tag' }, l)) : []);
    otherNote.hidden = !other;
    chartAlt.textContent = result.rows.map((r) => `${r.label}: ${w(r.watt)} Watt, ${fmt(r.share * 100, 0)} Prozent`).join('; ');


    replace(
      tableSlot,
      hasRows
        ? Table({
            caption: 'Deine Geräte',
            className: 'tool-table--compact',
            columns: [
              { key: 'label', label: 'Kategorie', wide: true },
              { key: 'count', label: 'Anzahl', align: 'right' },
              { label: 'W je Gerät', align: 'right', render: (r) => [w(r.unitWatt) + ' ', badge(r.source)] },
              { label: 'W gesamt', align: 'right', render: (r) => w(r.watt) },
              { label: '€/Jahr', align: 'right', render: (r) => fmt(r.eur, 2) },
            ],
            rows: result.rows,
            footer: ['Summe', String(result.rows.reduce((s, r) => s + r.count, 0)), '', w(result.watt), fmt(result.eur, 2)],
          })
        : '',
    );
  }

  function plainText() {
    const lines = [`Standby-Rechner Smart Home – Strompreis ${fmt(result.price * 100, { max: 2 })} ct/kWh`];
    for (const r of result.rows) lines.push(`${r.count} × ${r.label}: ${w(r.watt)} W, ${fmt(r.eur, 2)} €/Jahr${r.hours !== 24 ? ` (${fmt(r.hours, { max: 1 })} h/Tag)` : ''}`);
    lines.push(`Grundlast: ${w(result.watt)} W · ${fmt(result.kwh, 1)} kWh/Jahr · ${fmt(result.eur, 2)} €/Jahr`);
    lines.push(pageUrl());
    return lines.join('\n');
  }

  function reset() {
    state = defaults(config);
    priceField.value = state.price * 100;
    hoursMode.value = 'all';
    for (const item of items) {
      controls[item.id].stepper.value = 0;
      controls[item.id].own.value = NaN;
      controls[item.id].ownWrap.hidden = true;
      controls[item.id].ownToggle.setAttribute('aria-expanded', 'false');
      syncItem(item);
    }
    syncGroupHours();
    track(TOOL, 'reset');
    recalc.flush();
  }

  replace(
    root,
    el(
      'form',
      { class: 'tool-form standby-form', onsubmit: (e) => e.preventDefault(), novalidate: true },
      settingsRow,
      el('div', { class: 'standby-groups' }, groupNodes),
      el('p', { class: 'tool-help' }, 'Batteriebetriebene Sensoren, Thermostate und Fernbedienungen fehlen hier bewusst – sie hängen nicht am Netz.'),
      ResetLink(reset),
    ),
    resultSection,
    Section('Deine Geräte', tableSlot),
    share.node,
    dock,
  );
  syncGroupHours();
  update();
  window.addEventListener('scroll', schedulePlace, { passive: true });
  window.addEventListener('resize', schedulePlace);
  schedulePlace();
  return { get state() { return state; }, get result() { return result; } };
}
